import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { userService } from '@cobu/api-client'
import type { RootState } from '../store'
import type { PublicProfileActionSheetRef } from '../components/public-profile/PublicProfileActionSheet'
import { Strings } from '../constants/Strings';
import { USER_KEYS, useBlockUserMutation } from './userHooks'
import { useChannelList } from './useChannelList'
import { logger } from '../utils/logger'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import { useResidentRestrictions } from '../hooks/useResidentRestrictions'
type PublicProfileGroup = { uuid: string; name: string; imageUrl?: string }

export interface UsePublicProfileScreenReturn {
  showImageModal: boolean
  showReportModal: boolean
  showBlockConfirm: boolean
  reportSuccessAlertOpen: boolean
  actionSheetRef: React.RefObject<PublicProfileActionSheetRef>
  isOwnProfile: boolean
  canMessage: boolean
  canReport: boolean

  profileData: { user: any; groups: PublicProfileGroup[]; sharedGroups: PublicProfileGroup[] } | undefined
  currentUser: any | null
  isLoading: boolean
  error: unknown

  handleManageProfile: () => void
  handleMessage: () => Promise<void>
  handleMoreOptions: () => void
  handleProfileImagePress: () => void
  handleReportUser: () => void
  handleCloseReportModal: () => void
  handleReportSuccess: () => void
  handleBlockUser: () => void
  handleConfirmBlockUser: () => Promise<void>
  handleGroupPress: (group: PublicProfileGroup) => void
  handleBackPress: () => void
  setShowImageModal: (show: boolean) => void
  setShowReportModal: (show: boolean) => void
  setShowBlockConfirm: (show: boolean) => void
  setReportSuccessAlertOpen: (show: boolean) => void
}

export function usePublicProfileScreen(): UsePublicProfileScreenReturn {
  const navigate = useNavigate()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const currentUser = useSelector((s: RootState) => s.auth.user)
  const strings = Strings.get();  
  const [showImageModal, setShowImageModal] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [showBlockConfirm, setShowBlockConfirm] = useState(false)
  const [reportSuccessAlertOpen, setReportSuccessAlertOpen] = useState(false)
  const actionSheetRef = useRef<PublicProfileActionSheetRef>(null)
  const blockUserMutation = useBlockUserMutation()
  const { showDM } = useFeatureFlags();
  const { canReport } = useResidentRestrictions();

  const { navigateToChannel, isStreamAvailable } = useChannelList({
    enabled: false,
  })

  const { data: rawProfileData, isLoading, error } = useQuery({
    queryKey: ['public-profile', id],
    queryFn: () => userService.getUserProfile(id as string),
    enabled: !!id,
    staleTime: 0,
  })
  const user = rawProfileData?.user
  const profileData = user
    ? { user, groups: (rawProfileData as any)?.groups ?? [], sharedGroups: (rawProfileData as any)?.sharedGroups ?? [] }
    : undefined

  const profileUserId = (user as any)?.id || user?.uuid

  const isOwnProfile = !!currentUser?.uuid && ((profileUserId && currentUser.uuid === profileUserId) || (user?.uuid && currentUser.uuid === user?.uuid) || (id && currentUser.uuid === id))
  const isResidentProfile = useMemo(() => {
    const role = (profileData as any)?.user?.role
    const roleName = typeof (role as any)?.name === 'string' ? (role as any).name.toUpperCase() : ''
    const roleValue = typeof (role as any)?.value === 'number' ? (role as any).value : undefined
    return roleValue === 0 || roleName === 'RESIDENT'
  }, [profileData?.user?.role])
  const canMessage = showDM && !isOwnProfile && isResidentProfile

  const handleManageProfile = useCallback(() => {
    if (id) {
      queryClient.invalidateQueries({
        queryKey: USER_KEYS.getPublicProfile(id),
      });
    }
    navigate('/public-profile/manage');
  }, [navigate, id, queryClient]);

  useEffect(() => {
    if (!isOwnProfile && error) {
      navigate(-1)
    }
  }, [error, isOwnProfile, navigate])

  const handleMoreOptions = useCallback(() => {
    if (actionSheetRef.current && typeof actionSheetRef.current.show === 'function') {
      actionSheetRef.current.show()
    }
  }, [])

  const handleProfileImagePress = useCallback(() => {
    setShowImageModal(true)
  }, [])

  const handleReportUser = useCallback(() => {
    setShowReportModal(true)
  }, [])

  const handleCloseReportModal = useCallback(() => {
    setShowReportModal(false)
  }, [])

  const handleReportSuccess = useCallback(() => {
    setShowReportModal(false)
    // Show success alert like comments do
    setReportSuccessAlertOpen(true)
  }, [])

  const handleBlockUser = useCallback(() => {
    // Show confirmation dialog first
    setShowBlockConfirm(true)
  }, [])

  const handleConfirmBlockUser = useCallback(async () => {
    const targetUserId = (profileData as any)?.user?.uuid || (profileData as any)?.user?.id
    const myId = currentUser?.uuid
    if (!targetUserId || !myId) {
      setShowBlockConfirm(false)
      return
    }

    try {
      await blockUserMutation.mutateAsync({ currentUserId: myId, userToBlock: targetUserId })
      setShowBlockConfirm(false)
      // Invalidate queries to refresh UI
      await queryClient.invalidateQueries({ queryKey: ['blocked-users'] })
      await queryClient.invalidateQueries({ queryKey: ['public-profile', id] })
      // Navigate back after successful block
      navigate(-1)
    } catch (error) {
      logger.error('Failed to block user:', error)
      setShowBlockConfirm(false)
    }
  }, [profileData, currentUser, blockUserMutation, queryClient, id, navigate])

  const handleMessage = useCallback(async () => {
    const targetUser = profileData?.user
    if (!targetUser) {
      logger.error('Cannot open DM: user not found')
      return
    }
    if (!canMessage) {
      return
    }

    try {
      if (!isStreamAvailable) {
        logger.error('Stream not available')
        return
      }
      const targetUserId = targetUser.uuid || (targetUser as any)?.id
      const displayName = targetUser.displayName || `${targetUser.firstName || ''} ${targetUser.lastName || ''}`.trim()
      
      const channel = await navigateToChannel(targetUserId, displayName)
      const target = channel?.cid || channel?.id
      if (target) {
        navigate(`/chat/${target}`)
      } else {
        logger.error('Failed to create/get channel')
      }
    } catch (err: any) {
      logger.error('onDMPress error:', err)
    }
  }, [profileData, isStreamAvailable, navigateToChannel, navigate])

  const handleGroupPress = useCallback((group: PublicProfileGroup) => {
    if (group?.uuid) {
      navigate(`/groups/${group.uuid}`)
    }
  }, [navigate])

  const handleBackPress = useCallback(() => {
    navigate(-1)
  }, [navigate])

  return {
    showImageModal,
    showReportModal,
    showBlockConfirm,
    actionSheetRef,
    isOwnProfile,
    canMessage,
    canReport,

    profileData,
    currentUser,
    isLoading,
    error,

    handleManageProfile,
    handleMessage,
    handleMoreOptions,
    handleProfileImagePress,
    handleReportUser,
    handleCloseReportModal,
    handleReportSuccess,
    handleBlockUser,
    handleConfirmBlockUser,
    handleGroupPress,
    handleBackPress,
    setShowImageModal,
    setShowReportModal,
    setShowBlockConfirm,
    reportSuccessAlertOpen,
    setReportSuccessAlertOpen,
  }
}